// Libraries
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactQuill from 'react-quill'; // Import React Quill
import styled from 'styled-components';
import 'react-quill/dist/quill.snow.css'; // Import Quill styles

// Components
import { ToolCommentIcon } from 'components/Tools';
import bfbTheme from 'styles/b4b-theme';
import { htmlToPlainText } from './utils';

const truncateToPlainText = (html, maxLength) => {
  // Extract plain text using the same method as validation
  const plainText = htmlToPlainText(html);

  if (plainText.length <= maxLength) {
    return html;
  }

  // Truncate plain text
  const truncatedText = plainText.substring(0, maxLength);

  // Convert back to basic HTML preserving line breaks
  return truncatedText
    .split('\n')
    .filter(line => line.trim())
    .map(line => `<p>${line.trim()}</p>`)
    .join('');
};

function TextEditor({
  className = '',
  highlightComments = false,
  initialHTML = '',
  onBlur = null,
  showComments = false,
  placeholder = '',
  editorStyle = {},
  handleCommentClick,
  id,
  max = undefined,
  showWarningAlert,
}) {
  const [value, setValue] = useState(initialHTML);
  useEffect(() => {
    setValue(initialHTML);
  }, [initialHTML]);

  const quillRef = React.useRef(null);

  const textContent = quillRef.current ? quillRef.current.getEditor().getText() : '';
  const inputLength = textContent.trim().length;
  const isMaxReached = max ? inputLength > max : false;

  const myCommentIconId = `text-editor-component-comment-icon-${id}`;

  const handleOnChange = (content, delta, source, editor) => {
    if (max) {
      const currentLength = editor.getText().trim().length;

      // Prevent input beyond the limit
      if (currentLength > max) {
        showWarningAlert(true);
        // Don't update the value, keep the previous state
        return;
      }
      showWarningAlert(false);
    }
    setValue(content);
  };

  const handleOnBlur = (previousRange, source, editor) => {
    if (onBlur) {
      let html = editor.getHTML();
      const textLength = htmlToPlainText(html).length;

      // If the text length exceeds the max, truncate it first.
      if (max && textLength > max) {
        html = truncateToPlainText(html, max);
      }

      // Create a temporary DOM element in memory to parse and manipulate the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const { body } = doc;

      // 1. REMOVE TEXT STYLES (color, background-color, font, etc.)
      body.querySelectorAll('*[style]').forEach(element => {
        element.style.removeProperty('color');
        element.style.removeProperty('background-color');
        element.style.removeProperty('font-family');
        element.style.removeProperty('font-size');

        // If the style attribute is now empty, remove it completely for cleaner HTML
        if (!element.getAttribute('style').trim()) {
          element.removeAttribute('style');
        }
      });

      // 2. REMOVE HEADER ELEMENTS by replacing them with paragraphs
      body.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(header => {
        const p = doc.createElement('p');
        p.innerHTML = header.innerHTML;
        header.parentNode.replaceChild(p, header);
      });

      const cleanedHtml = body.innerHTML;
      // A re-render to update counter
      setValue(cleanedHtml);

      onBlur(cleanedHtml);
    }
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          ['bold', 'italic', 'underline', 'strike'],
          [{ script: 'sub' }, { script: 'super' }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link'],
          [{ history: 'undo' }, { history: 'redo' }],
        ],
      },
      history: {
        delay: 500,
        maxStack: 100,
        userOnly: true,
      },
    }),
    []
  );

  return (
    <StyledTextEditor
      className={className}
      $isMaxReached={isMaxReached}
      $minHeight={editorStyle.minHeight}
      $maxHeight={editorStyle.maxHeight}
    >
      <div className="editor-container">
        <ReactQuill
          ref={quillRef}
          style={editorStyle}
          value={value}
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          placeholder={placeholder}
          modules={modules}
        />
        {max && <span className="char-counter">{`${inputLength}/${max}`}</span>}
      </div>

      {showComments && (
        <aside>
          <i
            style={{
              paddingLeft: '10px',
            }}
            id={myCommentIconId}
          >
            <ToolCommentIcon active={highlightComments} onClick={handleCommentClick} />
          </i>
        </aside>
      )}
    </StyledTextEditor>
  );
}

const StyledTextEditor = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;

  .editor-container {
    flex: 1;
    min-width: calc(100% - 40px);
  }

  /* Targeting Quill's main container */
  .ql-container {
    border: 1px solid ${({ $isMaxReached }) => ($isMaxReached ? bfbTheme.error : '#E0E0E0')};
    border-radius: 4px;
    border-top: none; /* The toolbar has the top border */
  }

  .ql-toolbar {
    border: 1px solid ${({ $isMaxReached }) => ($isMaxReached ? bfbTheme.error : '#E0E0E0')};
    border-bottom: 1px solid #e0e0e0;
    border-radius: 4px 4px 0 0;
  }

  .ql-editor {
    overflow: auto;
    padding: 6px 8px;
    min-height: ${({ $minHeight }) => `${$minHeight}px` || '100px'};
    max-height: ${({ $maxHeight }) => `${$maxHeight}px` || 'none'};
  }

  .char-counter {
    float: right;
    font-size: 14px;
    margin-top: 4px;
    color: ${({ $isMaxReached }) => ($isMaxReached ? bfbTheme.error : bfbTheme.grey)};
  }
`;

TextEditor.propTypes = {
  className: PropTypes.string,
  editorStyle: PropTypes.instanceOf(Object),
  handleCommentClick: PropTypes.func.isRequired,
  highlightComments: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  initialHTML: PropTypes.string,
  max: PropTypes.number,
  onBlur: PropTypes.func,
  showComments: PropTypes.bool,
  showWarningAlert: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

export default TextEditor;
